import * as React from "react";
import { useDialogState } from "hooks/useDialogState";
import { useMutation, useQuery } from "react-query";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { DataTable } from "components/DataTable/DataTable";
import { DataTableRowAction } from "components/DataTable/types";
import { Button, DialogContentText } from "@mui/material";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { ClientConfig, ClientsService, PowerBiReportDefinition } from "gen/clients";
import { CreatePowerBiReportPanel } from "./CreatePowerBiReportPanel";
import { EditPowerBiReportPanel } from "./EditPowerBiReportPanel";
import { useClientReadOnly } from "contexts/ClientLockContext";

interface Props {
  clientId: string;
  onSaveSuccess: () => void;
}

const PowerBiReportsSettings: React.FC<Props> = ({ clientId, onSaveSuccess }) => {
  const isReadOnly = useClientReadOnly();
  const [isCreatePanelOpen, openCreatePanel, closeCreatePanel] = useDialogState();
  const [isEditPanelOpen, openEditPanel, closeEditPanel] = useDialogState();
  const [isConfirmDeleteOpen, openConfirmDelete, closeConfirmDelete] = useDialogState();
  const [selectedReport, setSelectedReport] = React.useState<PowerBiReportDefinition>();

  const {
    data: clientConfig,
    isLoading,
    error,
    refetch
  } = useQuery([clientId], {
    queryFn: () => ClientsService.getClientConfig({ id: clientId })
  });

  const powerBiReports = React.useMemo(() => clientConfig?.powerBiReports || [], [clientConfig]);

  const {
    mutate,
    error: mutationError,
    isLoading: isSaving
  } = useMutation({
    onSuccess: onSaveSuccess,
    mutationFn: ClientsService.updateClientConfig
  });

  const onDeleteClick = React.useCallback(
    (report: PowerBiReportDefinition) => {
      setSelectedReport(report);
      openConfirmDelete();
    },
    [openConfirmDelete]
  );

  const onEditClick = React.useCallback(
    (report: PowerBiReportDefinition) => {
      setSelectedReport(report);
      openEditPanel();
    },
    [openEditPanel]
  );

  const deleteSelectedReport = React.useCallback(() => {
    if (!clientConfig || !selectedReport) {
      return;
    }
    const updatedReports = powerBiReports.filter(report => report.id !== selectedReport.id);
    mutate(
      {
        id: clientConfig.id,
        requestBody: { ...clientConfig, powerBiReports: updatedReports }
      },
      {
        onSuccess: () => {
          setSelectedReport(undefined);
          refetch();
          closeConfirmDelete();
        }
      }
    );
  }, [clientConfig, selectedReport, powerBiReports, mutate, refetch, closeConfirmDelete]);

  const onMoveUpClick = React.useCallback(
    (report: PowerBiReportDefinition) => {
      if (!clientConfig) {
        return;
      }
      const updatedReports = [...powerBiReports];
      const oldIndex = updatedReports.findIndex(item => item.id === report.id);
      updatedReports.splice(oldIndex, 1);
      updatedReports.splice(oldIndex - 1, 0, report);
      mutate(
        {
          id: clientConfig.id,
          requestBody: { ...clientConfig, powerBiReports: updatedReports }
        },
        {
          onSuccess: () => refetch()
        }
      );
    },
    [clientConfig, powerBiReports, mutate, refetch]
  );

  const onMoveDownClick = React.useCallback(
    (report: PowerBiReportDefinition) => {
      if (!clientConfig) {
        return;
      }
      const updatedReports = [...powerBiReports];
      const oldIndex = updatedReports.findIndex(item => item.id === report.id);
      updatedReports.splice(oldIndex, 1);
      updatedReports.splice(oldIndex + 1, 0, report);
      mutate(
        {
          id: clientConfig.id,
          requestBody: { ...clientConfig, powerBiReports: updatedReports }
        },
        {
          onSuccess: () => refetch()
        }
      );
    },
    [clientConfig, powerBiReports, mutate, refetch]
  );

  const rowActions = React.useCallback(
    (report: PowerBiReportDefinition, rowIndex: number): DataTableRowAction[] => {
      const actions: DataTableRowAction[] = [
        {
          title: "Edit",
          action: onEditClick,
          disabled: isReadOnly
        }
      ];
      if (rowIndex > 0) {
        actions.push({
          title: "Move Up",
          action: onMoveUpClick,
          disabled: isReadOnly
        });
      }
      if (rowIndex < powerBiReports.length - 1) {
        actions.push({
          title: "Move Down",
          action: onMoveDownClick,
          disabled: isReadOnly
        });
      }
      actions.push({
        title: "Delete",
        action: onDeleteClick,
        color: "error.light",
        disabled: isReadOnly
      });
      return actions;
    },
    [onEditClick, onMoveUpClick, onMoveDownClick, onDeleteClick, powerBiReports.length, isReadOnly]
  );

  return (
    <>
      {mutationError && <SnackbarApiError error={mutationError} />}
      <Button
        color="primary"
        variant="contained"
        size="large"
        onClick={openCreatePanel}
        disabled={isReadOnly}
        sx={{ my: 2 }}
      >
        Add Power BI Report
      </Button>
      <DataTable
        columns={[
          {
            id: "name",
            title: "Report Name",
            cell: (report: PowerBiReportDefinition) => report.name
          },
          {
            id: "description",
            title: "Description",
            cell: (report: PowerBiReportDefinition) => report.description
          }
        ]}
        data={powerBiReports || []}
        rowKey={(report: PowerBiReportDefinition) => report.id}
        rowActions={rowActions}
        isLoading={isLoading}
        error={error && <ApiErrorMessage apiError={error} />}
      />

      {isCreatePanelOpen && clientConfig && (
        <CreatePowerBiReportPanel
          clientConfig={clientConfig as ClientConfig}
          powerBiReports={powerBiReports}
          open={isCreatePanelOpen}
          onClose={closeCreatePanel}
          onSuccess={() => {
            closeCreatePanel();
            onSaveSuccess();
            refetch();
          }}
        />
      )}

      {selectedReport && isEditPanelOpen && clientConfig && (
        <EditPowerBiReportPanel
          clientConfig={clientConfig as ClientConfig}
          powerBiReports={powerBiReports}
          editedReportIndex={powerBiReports.findIndex(report => report.id === selectedReport.id)}
          open={isEditPanelOpen}
          onClose={closeEditPanel}
          onSuccess={() => {
            closeEditPanel();
            onSaveSuccess();
            refetch();
          }}
        />
      )}

      {isConfirmDeleteOpen && (
        <ConfirmationDialog
          title="Delete Power BI Report?"
          content={<DialogContentText>Are you sure you want to delete this Power BI report?</DialogContentText>}
          onClose={isSaving ? () => undefined : closeConfirmDelete}
          onConfirm={deleteSelectedReport}
          isLoading={isSaving}
          error={mutationError ? <ApiErrorMessage apiError={mutationError} /> : undefined}
          confirmationButtonColor="error"
        />
      )}
    </>
  );
};

export { PowerBiReportsSettings };
